import firebase from "firebase/compat/app";
import { firestore } from "../firebaseConfig";
import { Timestamp, IUser, IUserPreferences } from "../types";
import { query, doc } from "firebase/firestore";

type DocumentSnapshot = firebase.firestore.DocumentSnapshot;
type SnapshotOptions = firebase.firestore.SnapshotOptions;

class User implements IUser {
  id: string;
  displayName: string | null;
  email: string;
  emailVerified: boolean;
  userId: string | null;
  isAdmin: boolean | false;
  createdAt?: Timestamp;
  preferences?: IUserPreferences;

  static collectionName = "users";

  static converter = {
    toFirestore(user: User) {
      return user.data();
    },
    fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions) {
      const data = snapshot.data(options) as IUser;
      return new User({ ...data, id: snapshot.id });
    },
  };

  static createId() {
    return firestore.collection(User.collectionName).doc().id;
  }

  constructor({
    id,
    displayName,
    email,
    emailVerified,
    userId,
    isAdmin,
    createdAt,
    preferences,
  }: IUser) {
    this.id = id;
    this.displayName = displayName || null;
    this.email = email;
    this.emailVerified = emailVerified;
    this.userId = userId || null;
    this.isAdmin = isAdmin || false;
    this.createdAt = createdAt;
    this.preferences = preferences;
  }

  clone() {
    return Object.assign(Object.create(Object.getPrototypeOf(this)), this);
  }

  setData(updates: Partial<IUser>) {
    return Object.assign(this, updates);
  }

  data() {
    return {
      displayName: this.displayName || "",
      email: this.email,
      emailVerified: !!this.emailVerified,
      userId: this.userId || "",
      isAdmin: this.isAdmin || false,
      createdAt: this.createdAt || null,
      preferences: this.preferences || null,
    };
  }

  static defaultUser = (): IUser => {
    return {
      id: "",
      displayName: "",
      email: "",
      emailVerified: false,
      userId: "",
      isAdmin: false,
      createdAt: undefined,
      preferences: undefined,
    };
  };

  static get = (email: string) => {
    return doc(
      firestore.collection(User.collectionName).withConverter(User.converter),
      email,
    );
  };

  static list = () => {
    return query(
      firestore
        .collection(User.collectionName)
        .withConverter(User.converter)
        .where("emailVerified", "==", false),
    );
  };

  static listAll = () => {
    return query(
      firestore.collection(User.collectionName).withConverter(User.converter),
    );
  };

  static create = async (data: Omit<IUser, "id" | "createdAt">) => {
    const newUser = new User({
      ...data,
      id: data.email,
      createdAt: Timestamp.now(),
    });

    await firestore
      .collection(User.collectionName)
      .doc(newUser.id)
      .withConverter(User.converter)
      .set(newUser, { merge: true });

    return newUser;
  };

  static update = async (user: User, updates: Partial<IUser>) => {
    const updatedUser = (user.clone && user.clone()) || new User({ ...user });

    updatedUser.setData({
      ...updates,
    });

    await firestore
      .collection(User.collectionName)
      .doc(user.id)
      .withConverter(User.converter)
      .set(updatedUser, { merge: true });

    return updatedUser;
  };

  static updatePreferences = async (
    email: string,
    preferences: Partial<IUserPreferences>,
  ) => {
    const updates = Object.fromEntries(
      Object.entries(preferences).map(([key, value]) => [
        `preferences.${key}`,
        value,
      ]),
    );
    await firestore.collection(User.collectionName).doc(email).update(updates);

    return preferences;
  };
}

export default User;
